import React, {useState} from 'react';
import {Link} from 'react-router-dom';
import {Box, Card, CardContent, Checkbox, Typography} from '@mui/material';
import {alpha} from '@mui/material/styles';
import {GalleryImage, photoUrl} from './GalleryImage';
import PhotoModal from './PhotoModal';

// 29.4: mentiunea afisata sub lucrare, dupa nivelul public (API-ul trimite varianta potrivita).
// 44.4: la teaser, „Detaliu din lucrare” cand artistul a ales un fragment clar, altfel previzualizarea blurata.
export const MENTIUNE_NIVEL = {teaser: 'Previzualizare înainte de dezvăluire', arhiva: 'Fotografie de arhivă'};
export const MENTIUNE_DETALIU = 'Detaliu din lucrare';

// 44.4: imaginea cardului — fragmentul clar (doar teaser + artist fara participari; API-ul decide) sau `poza`.
export const imagineCard = (work) => (work.nivel === 'teaser' && work.detaliu?.poza
    ? {src: work.detaliu.poza, detaliu: true} : {src: work.poza, detaliu: false});
export const mentiuneCard = (work) => (imagineCard(work).detaliu ? MENTIUNE_DETALIU : MENTIUNE_NIVEL[work.nivel]);

// Transparenta unitara a cardurilor din grila (admin + public): 30% transparent, 70% opac.
export const TRANSPARENTA_CARD = 0.3;

// Grila standard de lucrari (pasul 26), folosita si in #/admin, si pe paginile publice:
// carduri cu poza mare, click pe poza -> zoom in rama aurie cu navigare prin lucrarile
// din pagina. In admin, `selectable` adauga o bifa pe fiecare card (actiuni bulk).
// Optional: `continut(work)` - continut suplimentar sub card (ex. statusul si actiunile din pagina
// participarii); `fotografiiLucrare(work)` - zoom-ul arata toate fotografiile acelei lucrari, cu
// navigare intre ele, in loc de navigarea prin lucrarile din pagina.
// 54.5: `actiuni(work)` - actiunile cardului (Apreciaza / Distribuie), lipite de marginea de jos,
// ca sa fie pe acelasi rand in toate cardurile, oricat de lung e titlul.
export default function GrilaLucrari({
    lucrari, linkLucrare, linkArtist, selectable = false, selectedIds = [], onToggle,
    gol = 'Nicio lucrare încă.', titluZoom = 'Lucrări', continut, fotografiiLucrare, actiuni,
}) {
    const [selected, setSelected] = useState(null);
    const [zoomLucrare, setZoomLucrare] = useState(null); // {work, index} cand se folosesc fotografiile lucrarii
    if (!lucrari?.length) return <Typography color="text.secondary">{gol}</Typography>;
    // 29.4: serverul trimite deja varianta potrivita nivelului (teaser = blurata, arhiva = redusa cu sigla);
    // la teaser nu oferim zoom (nu are rost pe o imagine blurata). Mentiunea nivelului apare sub card.
    const esteTeaser = (work) => work.nivel === 'teaser';
    const photos = lucrari.filter((work) => work.poza && !esteTeaser(work)).map((work) => ({id: work.id, poza: work.poza, descriere: work.nume}));
    const index = photos.findIndex((photo) => photo.id === selected);
    const deschideZoom = (work) => {
        const fotografii = fotografiiLucrare?.(work);
        if (fotografii?.length) setZoomLucrare({work, index: 0});
        else setSelected(work.id);
    };
    const fotografiiZoom = zoomLucrare ? fotografiiLucrare(zoomLucrare.work) : [];
    return <>
        <Box className="grila-lucrari" sx={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 2, width: '100%'}}>
            {lucrari.map((work) => {
                const checked = selectable && selectedIds.includes(work.id);
                const detalii = [work.an, work.tehnica].filter(Boolean).join(' — ');
                const teaser = esteTeaser(work);
                const imagine = imagineCard(work);
                const mentiune = mentiuneCard(work);
                return <Card key={work.id} variant="outlined" sx={{position: 'relative', display: 'flex', flexDirection: 'column',
                    bgcolor: (theme) => alpha(theme.palette.background.paper, 1 - TRANSPARENTA_CARD),
                    ...(checked && {borderColor: 'primary.main', boxShadow: 2})}}>
                    {selectable && <Checkbox size="small" checked={checked} onChange={() => onToggle?.(work.id)}
                        inputProps={{'aria-label': `Selectează: ${work.nume}`}}
                        sx={{position: 'absolute', top: 4, left: 4, zIndex: 1, p: .5, bgcolor: 'background.paper', borderRadius: 1, '&:hover': {bgcolor: 'background.paper'}}} />}
                    {imagine.src && teaser ? <Box className={imagine.detaliu ? 'lucrare-detaliu' : 'lucrare-teaser'} sx={{height: 160, p: 1}}>
                        <GalleryImage src={photoUrl(imagine.src)} alt={`${work.nume} (${imagine.detaliu ? 'detaliu din lucrare' : 'previzualizare'})`} loading="lazy"
                            style={{width: '100%', height: '100%', objectFit: 'contain'}} />
                    </Box> : work.poza ? <button type="button" onClick={() => deschideZoom(work)} aria-label={`Mărește fotografia: ${work.nume}`}
                        style={{display: 'block', width: '100%', height: 160, border: 0, padding: 8, cursor: 'zoom-in', background: 'transparent'}}>
                        <GalleryImage src={photoUrl(work.poza)} alt={work.nume} loading="lazy" style={{width: '100%', height: '100%', objectFit: 'contain'}} />
                    </button> : <Box sx={{height: 160, display: 'grid', placeItems: 'center', color: 'text.secondary'}}>Fără fotografie</Box>}
                    <CardContent sx={{p: 1.5, '&:last-child': {pb: actiuni ? .5 : 1.5}, flexGrow: 1, display: 'flex', flexDirection: 'column'}}>
                        <Link to={linkLucrare(work)}>{work.nume}</Link>
                        {mentiune && <Typography variant="caption" color="text.secondary" display="block">{mentiune}</Typography>}
                        {detalii && <Typography variant="body2" color="text.secondary">{detalii}</Typography>}
                        {work.artist && linkArtist && <Typography variant="body2"><Link to={linkArtist(work)}>{work.artist}</Link></Typography>}
                        {fotografiiLucrare && (fotografiiLucrare(work)?.length || 0) > 1
                            && <Typography variant="caption" color="text.secondary" display="block">{fotografiiLucrare(work).length} fotografii</Typography>}
                        {continut?.(work)}
                        {actiuni && <Box sx={{mt: 'auto', pt: .5}}>{actiuni(work)}</Box>}
                    </CardContent>
                </Card>;
            })}
        </Box>
        {index >= 0 && <PhotoModal goldenFrame photos={photos} index={index} title={titluZoom}
            onSelect={(position) => setSelected(photos[position].id)} onClose={() => setSelected(null)} />}
        {zoomLucrare && <PhotoModal goldenFrame photos={fotografiiZoom} index={zoomLucrare.index} title={zoomLucrare.work.nume}
            onSelect={(position) => setZoomLucrare((z) => ({...z, index: position}))} onClose={() => setZoomLucrare(null)} />}
    </>;
}
